import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import EditIcon from '@/assets/icons/edit.svg';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/IconButton';
import { useProfileInstructors } from '@/features/profile/hooks/useProfileInstructors';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { EditInstructorsModal } from './EditInstructorsModal';
import { cn } from '@/lib/utils';
import { getInitials } from '@/utils/avatarUtils';

interface Instructor {
  id: string;
  instructor_user_id?: string;
  manual_name?: string;
  name?: string;
  username?: string;
  avatar_url?: string;
  belt_rank?: string;
  is_manual: boolean;
  created_at: string;
}

interface InstructorsSectionProps {
  profileId: string;
  isOwnProfile?: boolean;
}

export const InstructorsSection: React.FC<InstructorsSectionProps> = ({ 
  profileId, 
  isOwnProfile = false 
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { instructors, loading, error, refetch } = useProfileInstructors(profileId);
  const [showAll, setShowAll] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const { t } = useI18n();

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    // Refetch instructors when modal closes to ensure we have the latest data
    refetch();
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 w-20 bg-white/20 rounded mb-3"></div>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-white/20 rounded-full"></div>
            <div className="flex-1">
              <div className="h-4 bg-white/20 rounded w-3/4 mb-1"></div>
              <div className="h-3 bg-white/20 rounded w-1/2"></div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-white/20 rounded-full"></div>
            <div className="flex-1">
              <div className="h-4 bg-white/20 rounded w-2/3 mb-1"></div>
              <div className="h-3 bg-white/20 rounded w-1/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (instructors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4">
        {/* Empty State Icon */}
        <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
          <svg 
            className="w-10 h-10 text-white/40" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" 
            />
          </svg>
        </div>
        
        {/* Empty State Title */}
        <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
          {t('instructor.no_instructor_linked')}
        </h3>
        
        {/* Empty State Subtitle */}
        <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
          {t('instructor.empty_state_subtitle')}
        </p>
        
        {/* Call to Action Button */}
        {isOwnProfile && (
          <Button
            variant="outline"
            size="lg"
            onClick={handleEditClick}
            className="mt-8"
          >
            <img src={EditIcon} alt="" className="w-4 h-4" />
            {t('instructor.add_your_instructor')}
          </Button>
        )}
        
        {isOwnProfile && (
          <EditInstructorsModal
            isOpen={showEditModal}
            onClose={handleModalClose}
            profileId={profileId}
          />
        )}
      </div>
    );
  }

  const displayInstructors = showAll ? instructors : instructors.slice(0, 3);
  const hasMore = instructors.length > 3;

  return (
    <div className="relative w-full">
      <div className="flex items-center justify-end mb-3">
        {hasMore && !showAll && (
          <button 
            onClick={() => setShowAll(true)}
            className="text-sossiety-red hover:text-sossiety-red-hover text-sm font-medium"
          >
            {t('instructor.view_more')}
          </button>
        )}
      </div>
      
      {isOwnProfile && (
        <IconButton
          aria-label={t('instructor.edit_instructors')}
          onClick={handleEditClick}
          size="sm"
          className="absolute top-1/2 right-0 -translate-y-1/2"
        >
          <img src={EditIcon} alt="" className="w-4 h-4" />
        </IconButton>
      )}

      <div className="space-y-3 w-full">
        {displayInstructors.map((instructor) => {
          const isLinkable = !instructor.is_manual && instructor.username;
          
          const handleInstructorClick = () => {
            if (isLinkable && instructor.username) {
              navigate(`/profile/${instructor.username}`);
            }
          };

          return (
            <div 
              key={instructor.id} 
              className={cn(
                "flex items-center gap-3",
                isLinkable && "cursor-pointer hover:opacity-80 transition-opacity"
              )}
              onClick={isLinkable ? handleInstructorClick : undefined}
              role={isLinkable ? "button" : undefined}
              tabIndex={isLinkable ? 0 : undefined}
              onKeyDown={isLinkable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleInstructorClick();
                }
              } : undefined}
              aria-label={isLinkable ? `View ${instructor.name}'s profile` : undefined}
            >
              {instructor.is_manual ? (
                // Manual instructor - show initials in black frame
                <div className="w-7 h-7 bg-gray-900 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-medium">
                    {instructor.manual_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </span>
                </div>
              ) : (
                // sOSSiety user instructor - show avatar with belt frame
                <FramedAvatar
                  src={instructor.avatar_url}
                  alt={instructor.name || 'Instructor'}
                  beltRank={instructor.belt_rank}
                  size={28}
                  frameSize={{ w: 36, h: 40 }}
                  initials={getInitials(instructor.name, '')}
                />
              )}
              
              <div className="flex-1">
                <div className={cn(
                  "font-medium text-white font-roboto text-sm",
                  isLinkable && "hover:text-sossiety-red transition-colors"
                )}>
                  {instructor.is_manual ? instructor.manual_name : instructor.name}
                </div>
                {!instructor.is_manual && instructor.username && (
                  <div className={cn(
                    "text-xs text-white/60 font-poppins",
                    isLinkable && "hover:text-sossiety-red transition-colors"
                  )}>
                    @{instructor.username}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showAll && hasMore && (
        <button 
          onClick={() => setShowAll(false)}
          className="text-sossiety-red hover:text-sossiety-red-hover text-sm font-medium mt-3"
        >
          {t('instructor.show_less')}
        </button>
      )}
      
      {isOwnProfile && (
        <EditInstructorsModal
          isOpen={showEditModal}
          onClose={handleModalClose}
          profileId={profileId}
        />
      )}
    </div>
  );
}; 